/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import { StyledModal, ModalActions, Button, ButtonGroup } from 'sarsaparilla';

type SharedCaptchaModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'full';

interface SharedCaptchaModalProps {
    /** Controls whether the modal is visible. */
    isOpen?: boolean;
    /** Called when the user clicks the primary Continue button. */
    onContinue: () => void;
    /** Called when the modal should close (cancel button, close icon, overlay if enabled). */
    onRequestClose: () => void;
    /** Visible modal title text announced by assistive technologies. */
    heading?: string;
    /** Continue button text label. */
    continueButtonText?: string;
    /** Optional cancel button text label. */
    cancelButtonText?: string;
    /** Shows or hides the cancel button. */
    showCancelButton?: boolean;
    /** Shows or hides the close icon in the modal header. */
    showCloseIcon?: boolean;
    /** Disables the Continue button when true. */
    isContinueDisabled?: boolean;
    /** Shows loading state on the Continue button when true. */
    isContinueLoading?: boolean;
    /** Allows closing modal by clicking overlay when true. */
    shouldCloseOnOverlayClick?: boolean;
    /** Modal width preset. */
    size?: SharedCaptchaModalSize;
    /** Custom modal body content (for example, RecaptchaCheckbox). */
    children?: React.ReactNode;
}

export function SharedCaptchaModal({
    isOpen = false,
    onContinue,
    onRequestClose,
    heading = "We need to verify that you're a human",
    continueButtonText = 'Continue',
    cancelButtonText = 'Cancel',
    showCancelButton = false,
    showCloseIcon = false,
    isContinueDisabled = false,
    isContinueLoading = false,
    shouldCloseOnOverlayClick = false,
    size = 'md',
    children = null,
}: SharedCaptchaModalProps) {
    return (
        <StyledModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            shouldShowCloseButton={showCloseIcon}
            heading={heading}
            size={size}
        >
            {children}
            <ModalActions>
                <ButtonGroup isStretchedToFit>
                    {showCancelButton && (
                        <Button appearance="tertiary" onClick={onRequestClose}>
                            {cancelButtonText}
                        </Button>
                    )}
                    <Button
                        onClick={onContinue}
                        isDisabled={isContinueDisabled}
                        isLoading={isContinueLoading}
                    >
                        {continueButtonText}
                    </Button>
                </ButtonGroup>
            </ModalActions>
        </StyledModal>
    );
}

export default SharedCaptchaModal;
